import React, { useState, useRef, useEffect } from "react";

const Tooltip = ({ content, children, position = "top", className = "" }) => {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});

  const calculateTooltipPosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      try {
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const offset = 8; // offset between tooltip and trigger element
        const viewportWidth = window.innerWidth;
        const maxTooltipWidth = window.innerWidth - 100; // Maximum width for tooltip
        
        let style = {};
        let left = rect.left + rect.width / 2;
        let top = rect.top - offset;
        let transform = "translate(-50%, -100%)";
        
        switch (position) {
          case "top":
            left = rect.left + rect.width / 2;
            top = rect.top - offset;
            transform = "translate(-50%, -100%)";
            break;
          case "bottom":
            left = rect.left + rect.width / 2;
            top = rect.bottom + offset;
            transform = "translate(-50%, 0)";
            break;
          case "left":
            left = rect.left - offset;
            top = rect.top + rect.height / 2;
            transform = "translate(-100%, -50%)";
            break;
          case "right":
            left = rect.right + offset;
            top = rect.top + rect.height / 2;
            transform = "translate(0, -50%)";
            break;
          default:
            left = rect.left + rect.width / 2;
            top = rect.top - offset;
            transform = "translate(-50%, -100%)";
        }

        // Check horizontal overflow for top/bottom positions
        if (position === "top" || position === "bottom") {
          const tooltipWidth = Math.min(tooltipRect.width, maxTooltipWidth);
          const halfWidth = tooltipWidth / 2;
          
          // Check if tooltip would overflow on the right
          if (left + halfWidth > viewportWidth - 10) {
            // Align to right edge of trigger element
            left = rect.right;
            transform = position === "top" ? "translate(-100%, -100%)" : "translate(-100%, 0)";
          }
          // Check if tooltip would overflow on the left
          else if (left - halfWidth < 10) {
            // Align to left edge of trigger element
            left = rect.left;
            transform = position === "top" ? "translate(0, -100%)" : "translate(0, 0)";
          }
        }

        style = {
          left,
          top,
          transform,
          maxWidth: `${maxTooltipWidth}px`,
        };
        
        setTooltipStyle(style);
      } catch (error) {
        console.error("Error calculating tooltip position:", error);
      }
    }
  };

  // Add scroll and resize event listeners when tooltip is shown
  useEffect(() => {
    if (showTooltip) {
      // Recalculate position after tooltip renders
      const timer = setTimeout(() => {
        calculateTooltipPosition();
      }, 0);

      const handleScroll = () => {
        calculateTooltipPosition();
      };

      const handleResize = () => {
        calculateTooltipPosition();
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showTooltip]);

  const handleMouseEnter = () => {
    setShowTooltip(true);
    calculateTooltipPosition();
  };

  const handleMouseMove = () => {
    calculateTooltipPosition();
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Tooltip element
  const tooltipElement = showTooltip ? (
    <div
      ref={tooltipRef}
      style={{ 
        position: "fixed", 
        ...tooltipStyle, 
        zIndex: 9999,
        pointerEvents: "none"
      }}
      className={`${content ? "w-max bg-white text-black text-sm rounded shadow-lg px-3 py-2 border border-gray-200 transition-opacity duration-300" : "hidden"}`}
    >
      {typeof content === "string" ? (
        <span>
          <p className="font-normal text-black">{content}</p>
        </span>
      ) : (
        content
      )}
      {/* Tooltip arrow */}
      <div
        style={{ position: "absolute" }}
        className={content ? `bg-white w-2 h-2 rotate-45 ${
          position === "top"
            ? "top-full -mt-1 left-1/2 -translate-x-1/2"
            : position === "bottom"
            ? "bottom-full left-1/2 -translate-x-1/2 mt-1"
            : position === "left"
            ? "left-full top-1/2 -translate-y-1/2 -ml-1"
            : "right-full top-1/2 -translate-y-1/2 -mr-1"
        }` : "hidden"}
      ></div>
    </div>
  ) : null;

  return (
    <div className="relative inline-flex">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className={className}
      >
        {children}
      </div>
      {tooltipElement}
    </div>
  );
};

export default Tooltip;
